import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { MapPin, TrendingUp, Shield } from 'lucide-react';

const API = "https://safesheatlas.com/api";

const GlobalCoverageMap = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API}/rankings/global`);
      setDestinations(response.data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMarkerColor = (wsi) => {
    if (wsi >= 80) return '#10B981'; // Green - Low Risk
    if (wsi >= 60) return '#14B8A6'; // Teal - Moderate
    if (wsi >= 40) return '#F59E0B'; // Orange - Elevated
    return '#EF4444'; // Red - High Risk
  };

  const getMarkerSize = (wsi) => {
    // Larger markers for safer cities
    if (wsi >= 80) return 12;
    if (wsi >= 60) return 10;
    if (wsi >= 40) return 8;
    return 7;
  };

  const getRiskLabel = (wsi) => {
    if (wsi >= 80) return 'Low Risk';
    if (wsi >= 60) return 'Moderate';
    if (wsi >= 40) return 'Elevated';
    return 'High Risk';
  };

  if (loading) {
    return (
      <div className="h-[500px] bg-gray-100 rounded-2xl flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-600">Loading global coverage map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative z-0">
      <div className="h-[500px] rounded-2xl overflow-hidden shadow-xl border-2 border-purple-100 relative z-0">
        <MapContainer
          center={[20, 0]}
          zoom={2}
          minZoom={2}
          maxZoom={8}
          maxBounds={[[-90, -180], [90, 180]]}
          maxBoundsViscosity={1.0}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          worldCopyJump={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {destinations.map((item, idx) => {
            const dest = item.destination;
            const metrics = item.metrics;
            const wsi = metrics.composite_wsi;

            return (
              <CircleMarker
                key={dest.id}
                center={[dest.latitude, dest.longitude]}
                radius={getMarkerSize(wsi)}
                pathOptions={{
                  color: getMarkerColor(wsi),
                  fillColor: getMarkerColor(wsi),
                  fillOpacity: 0.8,
                  weight: 2
                }}
                eventHandlers={{
                  click: () => {
                    navigate(`/destination/${dest.id}`);
                  }
                }}
              >
                <Tooltip permanent={false} direction="top">
                  <div className="text-xs">
                    <strong>{dest.city}</strong>
                  </div>
                </Tooltip>
                
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <div className="flex items-start gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-purple-600 mt-1" />
                      <div>
                        <h3 className="font-bold text-base">{dest.city}</h3>
                        <p className="text-xs text-gray-600">{dest.country}</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-2 mb-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Safety Score</span>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-lg font-bold" 
                            style={{ color: getMarkerColor(wsi) }}
                          >
                            {wsi.toFixed(0)}
                          </span>
                          <span className="text-xs text-gray-500">/100</span>
                        </div>
                      </div>
                      <div className="mt-1">
                        <span 
                          className="inline-block px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: getMarkerColor(wsi) + '20',
                            color: getMarkerColor(wsi)
                          }}
                        >
                          {getRiskLabel(wsi)}
                        </span>
                      </div>
                    </div>

                    {metrics.trend_7day !== 0 && (
                      <div className="flex items-center gap-1 text-xs mb-2">
                        <TrendingUp 
                          className={`w-3 h-3 ${metrics.trend_7day > 0 ? 'text-green-600' : 'text-red-600 rotate-180'}`} 
                        />
                        <span className={metrics.trend_7day > 0 ? 'text-green-600' : 'text-red-600'}>
                          {metrics.trend_7day > 0 ? '+' : ''}{metrics.trend_7day.toFixed(1)}
                        </span>
                        <span className="text-gray-500">7-day trend</span>
                      </div>
                    )}
                    
                    <button
                      onClick={() => navigate(`/destination/${dest.id}`)}
                      className="w-full mt-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {/* Map Legend */}
      <div className="bg-gradient-to-br from-purple-50 to-teal-50 rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h4 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Global Safety Coverage
            </h4>
            <p className="text-sm text-gray-600">
              Tracking women's safety across {destinations.length} major destinations worldwide
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
              <span className="text-xs font-medium">Low Risk (80+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#14B8A6' }}></div>
              <span className="text-xs font-medium">Moderate (60-79)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }}></div>
              <span className="text-xs font-medium">Elevated (40-59)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EF4444' }}></div>
              <span className="text-xs font-medium">High Risk (&lt;40)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalCoverageMap;
